import { Link, useLocation } from "react-router";
import { useState } from "react";
import { Logo } from "./logo";
import { Sheet, SheetContent, SheetTrigger, SheetClose } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { AccessRequestModal } from "@/components/access-request-modal";
import { cn } from "@/lib/utils";

const NAV_LINKS = [
  { href: "/product", label: "Product" },
  { href: "/about", label: "About" },
] as const;

export function Header() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [accessOpen, setAccessOpen] = useState(false);
  const location = useLocation();

  return (
    <header className="sticky top-0 z-50 border-b border-border bg-background">
      <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-4">
        <Link
          to="/"
          aria-label="Truspecta home"
          className="flex min-h-11 items-center"
        >
          <Logo />
        </Link>

        {/* Desktop nav */}
        <nav aria-label="Main" className="hidden items-center gap-6 md:flex">
          {NAV_LINKS.map((link) => (
            <Link
              key={link.href}
              to={link.href}
              aria-current={location.pathname === link.href ? "page" : undefined}
              className={cn(
                "relative min-w-11 px-1 py-3 text-center text-sm text-foreground-muted transition-colors hover:text-foreground after:absolute after:bottom-0 after:left-1 after:h-px after:w-[calc(100%-0.5rem)] after:origin-left after:bg-foreground after:transition-transform after:duration-200 hover:after:scale-x-100",
                location.pathname === link.href
                  ? "text-foreground after:scale-x-100"
                  : "after:scale-x-0",
              )}
            >
              {link.label}
            </Link>
          ))}
          <Button
            type="button"
            variant="outline"
            aria-haspopup="dialog"
            onClick={() => setAccessOpen(true)}
          >
            Request access
          </Button>
        </nav>

        {/* Mobile hamburger */}
        <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
          <SheetTrigger asChild>
            <Button variant="ghost" size="icon" className="md:hidden h-11 w-11" aria-label="Open menu">
              <svg
                className="h-5 w-5 text-foreground"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M4 6h16M4 12h16M4 18h16"
                />
              </svg>
            </Button>
          </SheetTrigger>
          <SheetContent
            side="right"
            aria-label="Mobile navigation"
            className="w-[min(20rem,calc(100vw-2rem))] p-5"
          >
            <nav aria-label="Main" className="flex flex-col gap-2 pt-8">
              {NAV_LINKS.map((link) => (
                <SheetClose key={link.href} asChild>
                  <Link
                    to={link.href}
                    aria-current={location.pathname === link.href ? "page" : undefined}
                    className={cn(
                      "block border-b border-border-subtle py-4 text-base font-medium transition-colors hover:text-primary",
                      location.pathname === link.href ? "text-primary" : "text-foreground",
                    )}
                  >
                    {link.label}
                  </Link>
                </SheetClose>
              ))}
              <Button
                type="button"
                className="mt-4 self-start"
                aria-haspopup="dialog"
                onClick={() => {
                  setMobileOpen(false);
                  setAccessOpen(true);
                }}
              >
                Request access
              </Button>
            </nav>
          </SheetContent>
        </Sheet>
      </div>
      <AccessRequestModal open={accessOpen} onOpenChange={setAccessOpen} />
    </header>
  );
}
